import React from 'react';
import { Form, Input, Button, FormInstance } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useTranslation } from 'react-i18next';
import getDashboardStyle from '../utils/getDashboardStyle';

export type LoginFormValues = {
  username: string;
  password: string;
  remember?: boolean;
};

type LoginFormProps = {
  /** Called with the entered credentials on a valid submit. */
  onFinish: (values: LoginFormValues) => void;
  onFinishFailed?: (errorInfo: ValidateErrorEntity) => void;
  /** Optional external form instance (e.g. to prefill the username). */
  form?: FormInstance;
  /** Shows a spinner on the submit button while logging in. */
  loading?: boolean;
  initialValues?: object;
  style?: React.CSSProperties;
  /**
   * Extra items rendered between the password field and the submit button
   * (e.g. the "remember me" checkbox on the login page).
   */
  children?: React.ReactNode;
};

/**
 * Shared username/password login form used by both the full Login page and the
 * session-expired AuthModal, so the fields, validation and styling stay in sync.
 */
const LoginForm = ({
  onFinish,
  onFinishFailed,
  form,
  loading,
  initialValues,
  style,
  children,
}: LoginFormProps) => {
  const { t } = useTranslation();
  const mainColor = getDashboardStyle().mainColor;

  return (
    <Form
      form={form}
      name="basic"
      wrapperCol={{ span: 24 }}
      initialValues={initialValues}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      style={style}
    >
      <Form.Item
        name="username"
        rules={[{ required: true, message: t('general.inputError.empty') }]}
      >
        <Input
          placeholder={
            getDashboardStyle().isSalesForceAppType
              ? t('users.userListColumns.mobilePhone')
              : t('login.username')
          }
          size="large"
          prefix={<UserOutlined style={{ color: mainColor }} />}
        />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[{ required: true, message: t('general.inputError.empty') }]}
      >
        <Input.Password
          placeholder={t('login.password')}
          size="large"
          prefix={<LockOutlined style={{ color: mainColor }} />}
        />
      </Form.Item>
      {children}
      <Form.Item style={{ marginBottom: 0 }}>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          loading={loading}
        >
          {t('login.title')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default LoginForm;
